import { buildControllerRules } from "@gettactic/helpers/src/hook-form/buildControllerRules";
import { buildErrorMessageForName } from "@gettactic/helpers/src/hook-form/buildErrorMessageForName";
import {
  dateToTimeString,
  timeStringToDate
} from "@gettactic/helpers/src/date-time";
import React from "react";
import { Controller, FieldValues } from "react-hook-form";
import { TimeSelect } from "./TimeSelect";
import { HookFormTimeSelectProps, HookFormTimeSelectDateProps } from "./types";

/**
 * HookFormTimeSelect for forms that store time as strings (e.g., "09:00 AM")
 */
export const HookFormTimeSelect = <T extends FieldValues>({
  form,
  name,
  rules,
  onChange,
  ...props
}: HookFormTimeSelectProps<T>): React.ReactElement => {
  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field, fieldState }) => {
        const value = typeof field.value === "string" ? field.value : "";
        return (
          <TimeSelect
            {...props}
            value={value}
            onSelect={(newValue) => {
              field.onChange(newValue ?? "");
              onChange?.(newValue ?? "");
            }}
            error={
              fieldState.error?.message ?? buildErrorMessageForName(form, name)
            }
          />
        );
      }}
    />
  );
};

/**
 * HookFormTimeSelectDate for forms that store time as Date objects.
 * Automatically converts between Date objects and time strings.
 */
export const HookFormTimeSelectDate = <T extends FieldValues>({
  form,
  name,
  rules,
  onChange,
  ...props
}: HookFormTimeSelectDateProps<T>): React.ReactElement => {
  return (
    <Controller
      control={form.control}
      name={name}
      rules={buildControllerRules({
        form,
        name,
        rules,
        isRequired: props.isRequired
      })}
      render={({ field, fieldState }) => {
        // Convert Date to string for display
        const fieldValue = field.value as unknown;
        const value =
          fieldValue instanceof Date && !isNaN(fieldValue.getTime())
            ? dateToTimeString(fieldValue)
            : "";
        return (
          <TimeSelect
            {...props}
            value={value}
            onSelect={(newValue) => {
              if (newValue) {
                const dateValue = timeStringToDate(newValue);
                field.onChange(dateValue);
                onChange?.(dateValue);
              }
            }}
            error={
              fieldState.error?.message ?? buildErrorMessageForName(form, name)
            }
          />
        );
      }}
    />
  );
};
